import { FilterDocument, GroupByMode, noGroupingId } from '@griply/types';
import { getNewTaskPrefill, onNoGroupingReorder } from '@griply/utils';
import { useUpdateTasks } from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import {
  RenderItemProps,
  rootId,
  TaskListItemTypes,
  TreeItem,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import { RelationshipUpdate } from 'shared/types/relationship';
import { Task, UpdateTask } from 'shared/types/task';
import { DueDisplayOptions } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';

import { TreeTaskCard } from '../tree-task-card';
import * as Styled from './no-grouping-tasks-list.style';

export type NoGroupingTaskListRendererProps = {
  treeData: Record<ID, TreeItem>;
  tasks: Task[];
  onReorder?: (
    updatedSections: { id: ID; taskSorting: ID[] }[],
    updatedTask: UpdateTask[],
    relationshipUpdates?: RelationshipUpdate[],
  ) => void;
  collapsedSections?: ID[];
  onSectionCollapse?: (sectionIds: ID[]) => void;
  scrollElement?: React.RefObject<HTMLDivElement>;
  denseFormat?: boolean;
  filterDocument?: FilterDocument;
  isContextMenuOpen?: boolean;
  defaultNewTaskDate?: Timestamp;
  goalId?: ID;
  lifeAreaId?: ID;
  tagId?: ID;
  displayDue?: DueDisplayOptions[];
  showRepeatingAsTodo?: boolean;
  hideNewTaskRow?: boolean;
};

const taskItemTypes = [TaskListItemTypes.Task];

const dropTypes = {
  [TaskListItemTypes.Root]: [TaskListItemTypes.Task],
  [TaskListItemTypes.Goal]: [],
  [TaskListItemTypes.Task]: [],
  [TaskListItemTypes.LifeArea]: [],
  [TaskListItemTypes.Priority]: [],
  [TaskListItemTypes.Date]: [],
  [TaskListItemTypes.DayPart]: [],
  [TaskListItemTypes.Tag]: [],
  [TaskListItemTypes.Type]: [],
  [TaskListItemTypes.NewTask]: [],
  [TaskListItemTypes.Section]: [],
};

export const NoGroupingTaskListRenderer: React.FC<
  NoGroupingTaskListRendererProps
> = ({
  treeData,
  tasks,
  onReorder,
  collapsedSections,
  onSectionCollapse,
  scrollElement,
  denseFormat,
  filterDocument,
  isContextMenuOpen,
  defaultNewTaskDate,
  goalId,
  lifeAreaId,
  tagId,
  displayDue,
  showRepeatingAsTodo,
  hideNewTaskRow,
}) => {
  const today = useToday();

  const tasksMap = useMemo(() => {
    return tasks.reduce<Record<ID, Task>>((acc, task) => {
      acc[task.id] = task;
      return acc;
    }, {});
  }, [tasks]);

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }
    const result = onNoGroupingReorder({ updatedItems });
    if (!result) {
      return;
    }
    onReorder(result.updatedSections, result.updatedTasks);
  };

  // using refs to prevent the renderItem from rerendering all items
  const tasksMapRef = useRef(tasksMap);
  tasksMapRef.current = tasksMap;

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const renderItem: React.FC<RenderItemProps> = useMemo(
    () =>
      ({ item, tree, itemInstance, isMultiSelectModifierPressed }) => {
        if (item.type === TaskListItemTypes.NewTask && !hideNewTaskRow) {
          const prefill = getNewTaskPrefill({
            groupByMode: GroupByMode.None,
            groupSectionId: noGroupingId,
            groupSetting: { goalId, lifeAreaId, tagId },
            today,
            defaultNewTaskDate,
            filterDocument,
          });
          return (
            <NewTaskRow
              goal={prefill.goalId}
              lifeArea={prefill.lifeAreaId}
              tags={prefill.tagIds}
              startTime={null}
              duration={null}
              date={prefill.deadline}
            />
          );
        }

        const currentTasksMap = tasksMapRef.current;
        const currentTasks = tasksRef.current;

        return currentTasksMap[item.sourceId] ? (
          <TreeTaskCard
            task={currentTasksMap[item.sourceId]}
            tree={tree}
            item={itemInstance}
            tasks={currentTasks}
            isMultiSelectModifierPressed={isMultiSelectModifierPressed}
            displayDue={displayDue}
            showRepeatingAsTodo={showRepeatingAsTodo}
          />
        ) : null;
      },
    [
      defaultNewTaskDate,
      filterDocument,
      goalId,
      lifeAreaId,
      tagId,
      today,
      displayDue,
      showRepeatingAsTodo,
      hideNewTaskRow,
    ],
  );

  const [tree] = useTree();
  const { submit: updateTasks } = useUpdateTasks();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <VirtualizedTreeList
        rootItemId={rootId}
        items={treeData}
        renderItem={renderItem}
        scrollElement={scrollElement}
        multiselectTypes={taskItemTypes}
        dragTypes={taskItemTypes}
        onReorder={_onReorder}
        dropTypes={dropTypes}
        collapsedItems={collapsedSections}
        onCollapse={onSectionCollapse}
        denseFormat={denseFormat}
        isContextMenuOpen={isContextMenuOpen}
      />
    </Styled.Container>
  );
};
